import type { WorkflowView } from '@/lib/api/generated/model';

/**
 * A workspace's workflows, pinned ones first (spec 094 follow-up).
 *
 * **Reorders, never filters.** Every workflow that went in comes out, so the
 * sidebar's list still matches the page it links to — the property the merged
 * section was built on. Pinning moves a door to the top; it closes none.
 *
 * Pins are resolved against the live list rather than trusted, because the ids
 * live in this device's storage and outlive the things they name: a workflow
 * deleted on another machine leaves an id here that matches nothing. Filtering by
 * membership rather than looking each id up means such a pin simply has no effect,
 * which is the same treatment `resolveRecents` gives the palette's remembered
 * entries — and it needs no cleanup pass to stay true.
 *
 * Within each group the incoming order is preserved, so this composes with
 * whatever ordering the caller already applied rather than replacing it.
 */
export function pinnedFirst(
  workflows: WorkflowView[],
  pinnedIds: string[],
): WorkflowView[] {
  const pinned = new Set(pinnedIds);
  const top: WorkflowView[] = [];
  const rest: WorkflowView[] = [];

  for (const workflow of workflows) {
    (pinned.has(workflow.id) ? top : rest).push(workflow);
  }

  return [...top, ...rest];
}

/** Whether this workflow is pinned. One question, one answer, for both surfaces. */
export function isPinned(workflowId: string, pinnedIds: string[]): boolean {
  return pinnedIds.includes(workflowId);
}
